<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Group Visits</h5>
        <p class="page-sub">Create and manage multi-visitor invitations and recurring series</p>
      </div>
      <button class="btn-primary-action" @click="openCreate">
        <font-awesome-icon icon="users" /> New Group Visit
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input v-model="search" class="filter-input" placeholder="Search by name…" @input="loadGroups" />
      <input v-model="filterFrom" type="date" class="filter-input" @change="loadGroups" />
      <input v-model="filterTo"   type="date" class="filter-input" @change="loadGroups" />
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-wrap">
      <div v-for="n in 4" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!groups.length" class="empty-state">
      <font-awesome-icon icon="users" class="empty-icon" />
      <p>No group visits yet. Create one to invite multiple visitors at once.</p>
    </div>

    <!-- List -->
    <div v-else>
      <div v-for="g in groups" :key="g.id" class="group-card">
        <div class="group-card__header" @click="toggleExpand(g.id)">
          <div class="group-card__title-wrap">
            <div class="group-card__name">{{ g.name }}</div>
            <div class="group-card__meta">
              <span class="meta-chip">
                <font-awesome-icon icon="calendar-alt" /> {{ fmtDate(g.scheduled_at) }}
              </span>
              <span v-if="g.host" class="meta-chip">
                <font-awesome-icon icon="user-tie" /> {{ g.host.first_name }} {{ g.host.last_name }}
              </span>
              <span v-if="g.site" class="meta-chip">
                <font-awesome-icon icon="building" /> {{ g.site.name }}
              </span>
              <span v-if="g.series_id" class="meta-chip meta-chip--series">
                <font-awesome-icon icon="redo" /> Recurring series
              </span>
              <span class="badge badge--members">{{ g.visits_count }} member{{ g.visits_count !== 1 ? 's' : '' }}</span>
            </div>
          </div>
          <div class="group-card__actions" @click.stop>
            <button class="btn-icon" title="Edit" @click="openEdit(g)"><font-awesome-icon icon="edit" /></button>
            <button class="btn-icon btn-icon--danger" title="Cancel" @click="confirmCancel(g)"><font-awesome-icon icon="times-circle" /></button>
            <font-awesome-icon :icon="expanded === g.id ? 'chevron-up' : 'chevron-down'" class="expand-chevron" />
          </div>
        </div>

        <!-- Expanded member table -->
        <div v-if="expanded === g.id" class="group-card__body">
          <div v-if="loadingDetail" class="body-loading">Loading…</div>
          <template v-else-if="detail">
            <table class="member-table">
              <thead>
                <tr>
                  <th>Visitor</th><th>Email</th><th>Company</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="v in detail.visits" :key="v.id">
                  <td>{{ v.visitor?.first_name }} {{ v.visitor?.last_name }}</td>
                  <td>{{ v.visitor?.email || '—' }}</td>
                  <td>{{ v.visitor?.company || '—' }}</td>
                  <td><span :class="statusClass(v.status)" class="status-chip">{{ fmtStatus(v.status) }}</span></td>
                  <td>
                    <button
                      v-if="['pre_registered','pending_approval','approved'].includes(v.status)"
                      class="btn-remove"
                      title="Remove from group"
                      @click="confirmRemoveMember(g.id, v)"
                    ><font-awesome-icon icon="user-minus" /></button>
                  </td>
                </tr>
                <tr v-if="!detail.visits.length">
                  <td colspan="5" style="text-align:center;color:#aab8cc;padding:16px;">No members yet.</td>
                </tr>
              </tbody>
            </table>

            <div class="body-footer">
              <button class="btn-secondary-sm" @click="openAddMember(g)">
                <font-awesome-icon icon="user-plus" /> Add Member
              </button>
              <span v-if="detail.series_count > 1" class="series-info">
                Occurrence {{ seriesPosition(g) }} of {{ detail.series_count }} in series
                <button class="btn-link-danger" @click="confirmCancelSeries(g)">Cancel entire series</button>
              </span>
            </div>
          </template>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="meta.last_page > 1" class="pagination">
        <button :disabled="meta.current_page <= 1" @click="loadGroups(meta.current_page - 1)" class="page-btn">‹</button>
        <span class="page-info">{{ meta.current_page }} / {{ meta.last_page }}</span>
        <button :disabled="meta.current_page >= meta.last_page" @click="loadGroups(meta.current_page + 1)" class="page-btn">›</button>
      </div>
    </div>

    <!-- ── Create / Edit Modal ── -->
    <div v-if="modal" class="modal-backdrop" @click.self="modal = false">
      <div class="modal-box modal-box--lg">
        <div class="modal-header">
          <span>{{ editTarget ? 'Edit Group Visit' : 'New Group Visit' }}</span>
          <button class="modal-close" @click="modal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">

          <!-- Basic details -->
          <div class="form-grid-2">
            <div class="form-row">
              <label class="form-label">Group Name *</label>
              <input v-model="form.name" class="form-input" placeholder="e.g. Q4 Board Meeting" />
            </div>
            <div class="form-row">
              <label class="form-label">Host Employee *</label>
              <search-select
                v-model="form.host_employee_id"
                url="payroll/employees/active"
                query-param="search"
                label-key="full_name"
                sublabel-key="employee_no"
                value-key="id"
                response-key="data"
                placeholder="— Select host —"
                search-placeholder="Type name or number…"
                :min-chars="1"
                :initial-label="form.host_initial_label"
              />
            </div>
            <div class="form-row">
              <label class="form-label">Scheduled At *</label>
              <input v-model="form.scheduled_at" type="datetime-local" class="form-input" />
            </div>
            <div class="form-row">
              <label class="form-label">Visitor Type</label>
              <select v-model="form.visitor_type_id" class="form-input">
                <option value="">— Any —</option>
                <option v-for="t in visitorTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
              </select>
            </div>
            <div class="form-row">
              <label class="form-label">Site</label>
              <select v-model="form.site_id" class="form-input" @change="form.zone_id = ''">
                <option value="">— None —</option>
                <option v-for="s in sites" :key="s.id" :value="s.id">{{ s.name }}</option>
              </select>
            </div>
            <div class="form-row">
              <label class="form-label">Zone</label>
              <select v-model="form.zone_id" class="form-input" :disabled="!form.site_id">
                <option value="">— None —</option>
                <option v-for="z in zonesForSite" :key="z.id" :value="z.id">{{ z.name }}</option>
              </select>
            </div>
          </div>

          <div class="form-row" style="margin-top:12px;">
            <label class="form-label">Purpose</label>
            <input v-model="form.purpose" class="form-input" placeholder="Reason for visit" />
          </div>
          <div class="form-row" style="margin-top:12px;">
            <label class="form-label">Notes</label>
            <textarea v-model="form.notes" class="form-input" rows="2" style="resize:vertical;"></textarea>
          </div>

          <!-- Recurrence (create only) -->
          <template v-if="!editTarget">
            <hr class="section-divider" />
            <div class="section-label">Recurrence</div>
            <div class="setting-row">
              <div class="toggle-info">
                <span class="toggle-name">Recurring Series</span>
                <span class="toggle-hint">Repeat this group visit on a schedule</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" v-model="recurringEnabled" />
                <span class="toggle-track"></span>
              </label>
            </div>

            <div v-if="recurringEnabled" class="recurrence-grid" style="margin-top:14px;">
              <div class="form-row">
                <label class="form-label">Frequency</label>
                <select v-model="form.recurrence.frequency" class="form-input">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div class="form-row">
                <label class="form-label">Every</label>
                <div style="display:flex;align-items:center;gap:8px;">
                  <input v-model.number="form.recurrence.interval" type="number" min="1" max="12" class="form-input" style="width:70px;" />
                  <span class="form-hint">{{ form.recurrence.frequency === 'daily' ? 'day(s)' : form.recurrence.frequency === 'weekly' ? 'week(s)' : 'month(s)' }}</span>
                </div>
              </div>
              <div class="form-row">
                <label class="form-label">Occurrences</label>
                <input v-model.number="form.recurrence.count" type="number" min="2" max="52" class="form-input" style="width:80px;" />
              </div>
              <div class="form-row" style="align-self:flex-end;">
                <span class="recurrence-preview" v-if="recurrencePreview">{{ recurrencePreview }}</span>
              </div>
            </div>

            <template v-if="editTarget && editTarget.series_id">
              <div style="margin-top:10px;">
                <label class="form-label" style="display:flex;align-items:center;gap:8px;">
                  <input type="checkbox" v-model="form.update_series" />
                  Apply changes to all future occurrences in the series
                </label>
              </div>
            </template>
          </template>

          <!-- Visitors list (create only) -->
          <template v-if="!editTarget">
            <hr class="section-divider" />
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
              <div class="section-label" style="margin:0;">Visitors</div>
              <button class="btn-secondary-sm" type="button" @click="addVisitorRow">
                <font-awesome-icon icon="plus" /> Add Visitor
              </button>
            </div>

            <div v-for="(v, i) in form.visitors" :key="i" class="visitor-row">
              <input v-model="v.first_name" class="form-input" placeholder="First name *" />
              <input v-model="v.last_name"  class="form-input" placeholder="Last name *" />
              <input v-model="v.email"      class="form-input" placeholder="Email" />
              <input v-model="v.phone"      class="form-input" placeholder="Phone" />
              <input v-model="v.company"    class="form-input" placeholder="Company" />
              <button v-if="form.visitors.length > 1" class="btn-remove" @click="removeVisitorRow(i)">
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </template>

        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="modal = false">Cancel</button>
          <button class="btn-primary-action" @click="saveGroup" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Saving…' : editTarget ? 'Save Changes' : 'Create Group Visit' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Add Member Modal ── -->
    <div v-if="addMemberModal" class="modal-backdrop" @click.self="addMemberModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <span>Add Member</span>
          <button class="modal-close" @click="addMemberModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-grid-2">
            <div class="form-row">
              <label class="form-label">First Name *</label>
              <input v-model="memberForm.first_name" class="form-input" />
            </div>
            <div class="form-row">
              <label class="form-label">Last Name *</label>
              <input v-model="memberForm.last_name" class="form-input" />
            </div>
            <div class="form-row">
              <label class="form-label">Email</label>
              <input v-model="memberForm.email" class="form-input" type="email" />
            </div>
            <div class="form-row">
              <label class="form-label">Phone</label>
              <input v-model="memberForm.phone" class="form-input" />
            </div>
          </div>
          <div class="form-row" style="margin-top:12px;">
            <label class="form-label">Company</label>
            <input v-model="memberForm.company" class="form-input" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="addMemberModal = false">Cancel</button>
          <button class="btn-primary-action" @click="doAddMember" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Adding…' : 'Add Member' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Remove Member Confirm Modal ── -->
    <div v-if="removeModal" class="modal-backdrop" @click.self="removeModal = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-header modal-header--red">
          <div style="display:flex;align-items:center;gap:10px;">
            <div class="confirm-icon confirm-icon--red">
              <font-awesome-icon icon="user-minus" />
            </div>
            <span>Remove Visitor</span>
          </div>
          <button class="modal-close" @click="removeModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#4a5e78;line-height:1.6;">
            Remove <strong>{{ removeMemberTarget?.visit?.visitor?.first_name }} {{ removeMemberTarget?.visit?.visitor?.last_name }}</strong>
            from this group visit? Their invitation will be cancelled.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="removeModal = false">Keep</button>
          <button class="btn-danger" @click="doRemoveMember" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Removing…' : 'Remove Visitor' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Cancel Confirm Modal ── -->
    <div v-if="cancelModal" class="modal-backdrop" @click.self="cancelModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <span>Cancel Group Visit</span>
          <button class="modal-close" @click="cancelModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="color:#4a5e78;font-size:14px;">
            This will cancel all pre-registered visits in <strong>{{ cancelTarget?.name }}</strong>.
            Checked-in visitors are not affected.
          </p>
          <p v-if="cancelTarget?.series_id" style="margin-top:10px;">
            <label style="display:flex;align-items:center;gap:8px;font-size:14px;cursor:pointer;">
              <input type="checkbox" v-model="cancelSeries" />
              Cancel entire recurring series
            </label>
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="cancelModal = false">Keep</button>
          <button class="btn-danger" @click="doCancel" :disabled="saving">
            <font-awesome-icon v-if="saving" icon="spinner" spin />
            {{ saving ? 'Cancelling…' : 'Confirm Cancel' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import SearchSelect from '../../../../components/SearchSelect.vue'

const emptyMember = () => ({ first_name: '', last_name: '', email: '', phone: '', company: '' })
const emptyForm = () => ({
  name: '', host_employee_id: '', host_initial_label: '', purpose: '', scheduled_at: '', site_id: '',
  zone_id: '', visitor_type_id: '', notes: '',
  visitors: [emptyMember()],
  recurrence: { frequency: 'weekly', interval: 1, count: 4 },
  update_series: false,
})

export default {
  name: 'VisitorGroupVisits',
  components: { SearchSelect },
  setup() { return { toast: useToast() } },
  data() {
    return {
      loading: false, saving: false,
      groups: [], meta: {},
      search: '', filterFrom: '', filterTo: '',
      expanded: null, detail: null, loadingDetail: false,
      sites: [], visitorTypes: [],
      modal: false, editTarget: null, form: emptyForm(), recurringEnabled: false,
      addMemberModal: false, addMemberTarget: null,
      memberForm: emptyMember(),
      cancelModal: false, cancelTarget: null, cancelSeries: false,
      removeModal: false, removeMemberTarget: null,
    }
  },
  computed: {
    zonesForSite() {
      if (!this.form.site_id) return []
      const site = this.sites.find(s => s.id === this.form.site_id)
      return site?.zones ?? []
    },
    recurrencePreview() {
      if (!this.recurringEnabled || !this.form.scheduled_at || !this.form.recurrence.count) return ''
      const freq  = this.form.recurrence.frequency
      const n     = this.form.recurrence.count
      const int_  = this.form.recurrence.interval
      const label = freq === 'daily' ? `${int_} day(s)` : freq === 'weekly' ? `${int_} week(s)` : `${int_} month(s)`
      return `Creates ${n} occurrences, every ${label}`
    },
  },
  created() {
    this.loadGroups()
    this.loadDeps()
  },
  methods: {
    async loadGroups(page = 1) {
      this.loading = true
      try {
        const r = await axios.get('visitors/group-visits', {
          params: { page, search: this.search, from: this.filterFrom, to: this.filterTo },
        })
        this.groups = r.data.data.data
        this.meta   = r.data.data
      } catch { this.toast.error('Failed to load group visits.') }
      finally { this.loading = false }
    },
    async loadDeps() {
      try {
        const [sit, typ] = await Promise.all([
          axios.get('visitors/sites'),
          axios.get('visitors/types'),
        ])
        this.sites        = sit.data.data ?? []
        this.visitorTypes = typ.data.data ?? []
      } catch { /* non-critical — form options degrade gracefully */ }
    },
    async toggleExpand(id) {
      if (this.expanded === id) { this.expanded = null; this.detail = null; return }
      this.expanded     = id
      this.detail       = null
      this.loadingDetail = true
      try {
        const r = await axios.get(`visitors/group-visits/${id}`)
        this.detail = r.data.data
      } catch { this.toast.error('Failed to load group details.') }
      finally { this.loadingDetail = false }
    },
    openCreate() {
      this.editTarget       = null
      this.form             = emptyForm()
      this.recurringEnabled = false
      this.modal            = true
    },
    openEdit(g) {
      this.editTarget = g
      this.form = {
        ...emptyForm(),
        name:               g.name,
        host_employee_id:   g.host_employee_id,
        host_initial_label: g.host ? `${g.host.first_name} ${g.host.last_name}` : '',
        purpose:            g.purpose ?? '',
        scheduled_at:       g.scheduled_at ? g.scheduled_at.substring(0, 16) : '',
        site_id:            g.site_id ?? '',
        zone_id:            g.zone_id ?? '',
        visitor_type_id:    g.visitor_type_id ?? '',
        notes:              g.notes ?? '',
        update_series:      false,
      }
      this.modal = true
    },
    async saveGroup() {
      if (!this.form.name || !this.form.host_employee_id || !this.form.scheduled_at) {
        this.toast.error('Name, host and scheduled time are required.')
        return
      }
      if (!this.editTarget) {
        const missing = this.form.visitors.some(v => !v.first_name || !v.last_name)
        if (missing) { this.toast.error('Each visitor needs a first and last name.'); return }
      }
      this.saving = true
      try {
        const payload = { ...this.form }
        if (!this.recurringEnabled || this.editTarget) delete payload.recurrence
        if (this.editTarget) {
          await axios.put(`visitors/group-visits/${this.editTarget.id}`, payload)
          this.toast.success('Group visit updated.')
        } else {
          const r = await axios.post('visitors/group-visits', payload)
          const n = r.data.group_visit_ids?.length ?? 1
          this.toast.success(`${n} group visit${n > 1 ? 's' : ''} created.`)
        }
        this.modal = false
        this.loadGroups()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to save.')
      } finally { this.saving = false }
    },
    addVisitorRow() { this.form.visitors.push(emptyMember()) },
    removeVisitorRow(i) { this.form.visitors.splice(i, 1) },
    openAddMember(g) {
      this.addMemberTarget = g
      this.memberForm      = emptyMember()
      this.addMemberModal  = true
    },
    async doAddMember() {
      if (!this.memberForm.first_name || !this.memberForm.last_name) {
        this.toast.error('First and last name are required.'); return
      }
      this.saving = true
      try {
        await axios.post(`visitors/group-visits/${this.addMemberTarget.id}/members`, this.memberForm)
        this.toast.success('Member added.')
        this.addMemberModal = false
        this.toggleExpand(this.addMemberTarget.id)
        this.loadGroups()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to add member.')
      } finally { this.saving = false }
    },
    confirmRemoveMember(groupId, visit) {
      this.removeMemberTarget = { groupId, visit }
      this.removeModal = true
    },
    async doRemoveMember() {
      const { groupId, visit } = this.removeMemberTarget
      this.saving = true
      try {
        await axios.delete(`visitors/group-visits/${groupId}/members/${visit.id}`)
        this.toast.success('Member removed.')
        this.removeModal = false
        await this.toggleExpand(groupId)
        await this.toggleExpand(groupId)
        this.loadGroups()
      } catch { this.toast.error('Failed to remove member.') }
      finally { this.saving = false }
    },
    confirmCancel(g) {
      this.cancelTarget = g
      this.cancelSeries = false
      this.cancelModal  = true
    },
    confirmCancelSeries(g) {
      this.cancelTarget = g
      this.cancelSeries = true
      this.cancelModal  = true
    },
    async doCancel() {
      this.saving = true
      try {
        const url = this.cancelSeries
          ? `visitors/group-visits/${this.cancelTarget.id}/series`
          : `visitors/group-visits/${this.cancelTarget.id}`
        await axios.delete(url)
        this.toast.success(this.cancelSeries ? 'Series cancelled.' : 'Group visit cancelled.')
        this.cancelModal = false
        this.expanded    = null
        this.detail      = null
        this.loadGroups()
      } catch { this.toast.error('Failed to cancel.') }
      finally { this.saving = false }
    },
    seriesPosition() {
      if (!this.detail?.data) return '?'
      return this.detail.series_count
    },
    fmtDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },
    fmtStatus(s) {
      const map = {
        pre_registered: 'Pre-registered', pending_approval: 'Pending Approval',
        approved: 'Approved', checked_in: 'Checked In', checked_out: 'Checked Out',
        cancelled: 'Cancelled', denied_at_entry: 'Denied', no_show: 'No Show',
      }
      return map[s] ?? s
    },
    statusClass(s) {
      const map = {
        pre_registered: 'chip--blue', pending_approval: 'chip--yellow', approved: 'chip--green',
        checked_in: 'chip--teal', checked_out: 'chip--gray', cancelled: 'chip--red',
        denied_at_entry: 'chip--red', no_show: 'chip--orange',
      }
      return map[s] ?? 'chip--gray'
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.filter-bar   { display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px; }
.filter-input { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }

.group-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; margin-bottom:12px; overflow:hidden; }
.group-card__header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; cursor:pointer; transition:background .15s; }
.group-card__header:hover { background:#f8faff; }
.group-card__title-wrap { flex:1; min-width:0; }
.group-card__name { font-size:15px; font-weight:700; color:#1a2b45; margin-bottom:6px; }
.group-card__meta { display:flex; flex-wrap:wrap; gap:6px; align-items:center; }
.meta-chip { display:inline-flex; align-items:center; gap:4px; font-size:12px; color:#4a5e78; background:#f0f4f8; padding:3px 8px; border-radius:20px; }
.meta-chip--series { background:#eef2ff; color:#6366f1; }
.badge--members { background:#004AAD; color:#fff; font-size:11px; font-weight:700; padding:3px 9px; border-radius:20px; }

.group-card__actions { display:flex; align-items:center; gap:8px; }
.expand-chevron { font-size:12px; color:#7a90b0; margin-left:6px; }

.group-card__body { padding:0 20px 16px; border-top:1px solid #f0f4f8; }
.body-loading { padding:16px; text-align:center; color:#7a90b0; font-size:13px; }

.member-table { width:100%; border-collapse:collapse; font-size:13px; margin-top:12px; }
.member-table th { text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; color:#7a90b0; padding:6px 8px; border-bottom:1px solid #f0f4f8; }
.member-table td { padding:8px; border-bottom:1px solid #f8f9fc; color:#1a2b45; }
.member-table tr:last-child td { border-bottom:none; }

.body-footer { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; margin-top:12px; padding-top:10px; border-top:1px solid #f0f4f8; }
.series-info { font-size:12px; color:#7a90b0; }
.btn-link-danger { background:none; border:none; color:#ef4444; font-size:12px; cursor:pointer; text-decoration:underline; padding:0; margin-left:8px; font-family:inherit; }

.status-chip { display:inline-block; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--blue   { background:#eff6ff; color:#2563eb; }
.chip--yellow { background:#fffbeb; color:#d97706; }
.chip--green  { background:#f0fdf4; color:#16a34a; }
.chip--teal   { background:#f0fdfa; color:#0d9488; }
.chip--gray   { background:#f1f5f9; color:#64748b; }
.chip--red    { background:#fef2f2; color:#dc2626; }
.chip--orange { background:#fff7ed; color:#c2410c; }

/* Pagination */
.pagination { display:flex; align-items:center; justify-content:center; gap:12px; margin-top:20px; }
.page-btn  { padding:6px 14px; border:1px solid #dde5f0; border-radius:8px; background:#fff; cursor:pointer; font-size:14px; color:#4a5e78; }
.page-btn:disabled { opacity:.4; cursor:not-allowed; }
.page-info { font-size:13px; color:#7a90b0; }

/* Skeleton */
.sk-wrap { display:flex; flex-direction:column; gap:10px; }
.sk-row  { height:70px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:12px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Empty */
.empty-state { text-align:center; padding:60px 20px; color:#7a90b0; }
.empty-icon  { font-size:40px; margin-bottom:12px; opacity:.35; }

/* Modals */
.modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.4); display:flex; align-items:center; justify-content:center; z-index:9999; padding:16px; }
.modal-box  { background:#fff; border-radius:14px; width:100%; max-width:540px; max-height:90vh; display:flex; flex-direction:column; overflow:hidden; }
.modal-box--lg { max-width:760px; }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f4f8; font-size:15px; font-weight:700; color:#1a2b45; }
.modal-close { background:none; border:none; font-size:16px; color:#7a90b0; cursor:pointer; }
.modal-body  { flex:1; overflow-y:auto; padding:20px; }
.modal-footer { padding:16px 20px; border-top:1px solid #f0f4f8; display:flex; justify-content:flex-end; gap:10px; }

.form-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
@media(max-width:600px){ .form-grid-2 { grid-template-columns:1fr; } }
.form-row    { display:flex; flex-direction:column; gap:4px; }
.form-label  { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; width:100%; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.form-hint   { font-size:11px; color:#aab8cc; }
.section-divider { border:none; border-top:1px solid #f0f4f8; margin:16px 0; }
.section-label   { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:12px; }

.setting-row  { display:flex; justify-content:space-between; align-items:center; gap:16px; }
.toggle-info  { display:flex; flex-direction:column; gap:2px; }
.toggle-name  { font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-hint  { font-size:12px; color:#7a90b0; }
.toggle-switch { position:relative; display:inline-block; width:38px; height:22px; flex-shrink:0; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track  { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:22px; transition:.2s; }
.toggle-track::before { position:absolute; content:''; height:16px; width:16px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#004AAD; }
.toggle-switch input:checked + .toggle-track::before { transform:translateX(16px); }

.recurrence-grid  { display:grid; grid-template-columns:1fr 1fr 1fr 1fr; gap:12px; align-items:start; }
@media(max-width:600px){ .recurrence-grid { grid-template-columns:1fr 1fr; } }
.recurrence-preview { font-size:12px; color:#004AAD; font-weight:600; }

.visitor-row { display:grid; grid-template-columns:1fr 1fr 1fr 1fr 1fr auto; gap:8px; align-items:center; margin-bottom:8px; }
@media(max-width:700px){ .visitor-row { grid-template-columns:1fr 1fr; } }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#4a5e78; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; font-family:inherit; }
.btn-outline:hover { background:#f4f7fb; }
.btn-secondary-sm { display:inline-flex; align-items:center; gap:5px; background:#f4f7fb; color:#4a5e78; font-size:12px; font-weight:600; padding:0 12px; height:32px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; font-family:inherit; }
.btn-secondary-sm:hover { background:#e8eef8; }
.btn-icon { background:none; border:none; color:#7a90b0; font-size:14px; cursor:pointer; padding:4px 6px; border-radius:6px; }
.btn-icon:hover { background:#f0f4f8; color:#1a2b45; }
.btn-icon--danger:hover { background:#fef2f2; color:#dc2626; }
.btn-remove { background:none; border:none; color:#ef4444; font-size:13px; cursor:pointer; padding:4px 6px; border-radius:6px; }
.btn-remove:hover { background:#fef2f2; }
.btn-danger { display:inline-flex; align-items:center; gap:6px; background:#ef4444; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-danger:hover:not(:disabled) { background:#dc2626; }
.btn-danger:disabled { opacity:.6; cursor:not-allowed; }
.modal-box--sm { max-width:420px; }
.modal-header--red { background:#fff5f5; }
.modal-header--red span { color:#dc2626; font-weight:700; font-size:15px; }
.confirm-icon { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:15px; flex-shrink:0; }
.confirm-icon--red { background:#fee2e2; color:#dc2626; }
</style>
